import React, { useState, useEffect } from 'react';
import {
    useDynamicEntitiesContext,
    useDynamicEntitiesFetcher,
    useDynamicEntitiesPermissions
} from '../core/DynamicEntitiesProvider';
import { Input } from '../../Input/Input';
import { Button } from '../../Button/Button';
import { InformationCircleIcon, PencilSquareIcon, TrashIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface GenericListItem {
    id?: string;
    value: string;
    label: string;
    order: number;
    active: boolean;
}

interface GenericList {
    id?: string;
    code: string;
    description?: string;
    active: boolean;
    items: GenericListItem[];
}

export const CatalogManager: React.FC = () => {
    const { baseUrl, onError } = useDynamicEntitiesContext();
    const http = useDynamicEntitiesFetcher();
    const { canCreate, canUpdate, canDelete } = useDynamicEntitiesPermissions();
    const [lists, setLists] = useState<GenericList[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<Error | null>(null);
    const [editingList, setEditingList] = useState<GenericList | null>(null);

    const loadLists = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await http(`${baseUrl}/dynamic-entities/catalogs`);
            if (!res.ok) throw new Error(`Failed to fetch catalogs: ${res.status} ${res.statusText}`);
            const data = await res.json();
            setLists(data);
        } catch (err) {
            console.error(err);
            setLists([]);
            setLoadError(err as Error);
            onError?.(err as Error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLists();
    }, [baseUrl, http]);

    const handleSave = async () => {
        if (!editingList) return;
        const method = editingList.id ? 'PUT' : 'POST';
        const url = editingList.id
            ? `${baseUrl}/dynamic-entities/catalogs/${editingList.id}`
            : `${baseUrl}/dynamic-entities/catalogs`;

        try {
            const res = await http(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingList)
            });
            if (!res.ok) throw new Error(`Failed to save catalog: ${res.status} ${res.statusText}`);
            setEditingList(null);
            loadLists();
        } catch (err) {
            console.error(err);
            onError?.(err as Error);
            alert('Error al guardar catálogo');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('¿Eliminar catálogo?')) return;
        try {
            const res = await http(`${baseUrl}/dynamic-entities/catalogs/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error(`Failed to delete catalog: ${res.status} ${res.statusText}`);
            loadLists();
        } catch (err) {
            console.error(err);
            onError?.(err as Error);
        }
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex justify-between items-center mb-6">
                <div className="bg-[#cffafe] border border-[#67e8f9] flex items-center gap-2 px-4 py-2 rounded-lg">
                    <InformationCircleIcon className="w-6 h-6 text-[#0e7490]" />
                    <span className="text-sm text-[#0e7490]">Crea un listado de opciones</span>
                </div>
                {canCreate && (
                    <Button
                        type="outline"
                        onClick={() => setEditingList({ code: '', active: true, items: [] })}
                    >
                        Nuevo catálogo
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="text-gray-500 py-4">Cargando catálogos...</div>
            ) : loadError ? (
                <div
                    role="alert"
                    className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
                >
                    <ExclamationCircleIcon className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-1">
                        <p className="text-sm font-medium text-red-700">
                            No se pudieron cargar los catálogos.
                        </p>
                        <p className="text-sm text-red-600">{loadError.message}</p>
                    </div>
                </div>
            ) : (
                <div className="w-full overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr>
                                <th className="px-5 py-4 text-sm font-medium text-gray-400 border-b border-gray-200">Código</th>
                                <th className="px-5 py-4 text-sm font-medium text-gray-400 border-b border-gray-200">Nombre</th>
                                <th className="px-5 py-4 text-sm font-medium text-gray-400 border-b border-gray-200">Opciones</th>
                                <th className="px-5 py-4 text-sm font-medium text-gray-400 border-b border-gray-200 text-center w-[100px]">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lists.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-5 py-6 text-center text-gray-500">
                                        No has creado catálogos aún. Los catálogos son necesarios para campos de tipo Radio y Select.
                                    </td>
                                </tr>
                            )}
                            {lists.map(list => (
                                <tr key={list.id} className="group">
                                    <td className="px-5 py-4 text-sm text-gray-900 border-b border-gray-200">
                                        {list.code}
                                    </td>
                                    <td className="px-5 py-4 text-sm text-gray-900 border-b border-gray-200">
                                        {list.description || 'Sin descripción'}
                                    </td>
                                    <td className="px-5 py-4 text-sm border-b border-gray-200">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-normal bg-[#dbeefe] text-[#0e79fd]">
                                            {list.items?.length || 0} {list.items?.length === 1 ? 'Campo adicional' : 'Campos adicionales'}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-sm border-b border-gray-200 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            {canUpdate && (
                                                <Button
                                                    type="plain"
                                                    iconOnly
                                                    leftIcon={<PencilSquareIcon className="h-5 w-5" />}
                                                    ariaLabel="Editar"
                                                    onClick={() => setEditingList(list)}
                                                />
                                            )}
                                            {canDelete && (
                                                <Button
                                                    type="plain"
                                                    iconOnly
                                                    leftIcon={<TrashIcon className="h-5 w-5" />}
                                                    ariaLabel="Eliminar"
                                                    className="text-red-500"
                                                    onClick={() => list.id && handleDelete(list.id)}
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de Creación / Edición */}
            {editingList && (
                <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-200 relative w-full max-w-4xl flex flex-col max-h-[90vh] overflow-hidden">
                        {/* Close Button */}
                        <div className="absolute right-5 top-5 z-10">
                            <Button
                                type="plain"
                                size="xs"
                                iconOnly
                                leftIcon={<XMarkIcon className="w-5 h-5" />}
                                ariaLabel="Cerrar modal"
                                onClick={() => setEditingList(null)}
                            />
                        </div>

                        {/* Modal Content Scrollable */}
                        <div className="p-6 flex flex-col gap-6 overflow-y-auto">
                            {/* Header */}
                            <div className="flex items-center gap-4">
                                <div className="bg-[#fcedc9] w-10 h-10 rounded-full flex items-center justify-center shrink-0">
                                    <ExclamationCircleIcon className="w-6 h-6 text-yellow-700" />
                                </div>
                                <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                                    {editingList.id ? 'Editar catálogo' : 'Nuevo catálogo'}
                                </h2>
                            </div>

                            {/* Form Inputs */}
                            <div className="flex gap-5">
                                <div className="flex flex-col gap-2 w-64">
                                    <label className="text-sm font-medium text-gray-900 flex gap-1">
                                        Código <span className="text-red-300">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={editingList.code}
                                        onChange={e => setEditingList({ ...editingList, code: e.target.value })}
                                        disabled={!!editingList.id}
                                        placeholder="Ej: 91289421"
                                        className="border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500 disabled:bg-gray-50"
                                    />
                                </div>
                                <div className="flex flex-col gap-2 w-64">
                                    <label className="text-sm font-medium text-gray-900 flex gap-1">
                                        Nombre <span className="text-red-300">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={editingList.description || ''}
                                        onChange={e => setEditingList({ ...editingList, description: e.target.value })}
                                        placeholder="Ej: Estado del producto"
                                        className="border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Dynamic Table Area */}
                            <div className="flex flex-col items-end gap-5">
                                <Button
                                    type="outline"
                                    size="sm"
                                    onClick={() => {
                                        const newItems = [...editingList.items, { value: '', label: '', order: editingList.items.length, active: true }];
                                        setEditingList({ ...editingList, items: newItems });
                                    }}
                                >
                                    Nuevo
                                </Button>

                                <div className="w-full">
                                    {/* Table Header */}
                                    <div className="flex w-full border-b border-gray-200 min-h-[36px]">
                                        <div className="flex-1 px-4 py-2 text-sm font-medium text-gray-400 flex items-center">Valor ID</div>
                                        <div className="flex-1 px-4 py-2 text-sm font-medium text-gray-400 flex items-center">Etiqueta</div>
                                        <div className="w-[100px] px-4 py-2 text-sm font-medium text-gray-400 flex items-center justify-center">Acciones</div>
                                    </div>

                                    {/* Table Body */}
                                    {editingList.items.map((item, index) => (
                                        <div key={index} className="flex w-full border-b border-gray-200 py-2 items-center">
                                            <div className="flex-1 px-4">
                                                <input
                                                    type="text"
                                                    value={item.value}
                                                    onChange={e => {
                                                        const items = [...editingList.items];
                                                        items[index].value = e.target.value;
                                                        setEditingList({ ...editingList, items });
                                                    }}
                                                    className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500"
                                                    placeholder="Ej: john_smith"
                                                />
                                            </div>
                                            <div className="flex-1 px-4">
                                                <input
                                                    type="text"
                                                    value={item.label}
                                                    onChange={e => {
                                                        const items = [...editingList.items];
                                                        items[index].label = e.target.value;
                                                        setEditingList({ ...editingList, items });
                                                    }}
                                                    className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500"
                                                    placeholder="Ej: John Smith"
                                                />
                                            </div>
                                            <div className="w-[100px] px-4 flex items-center justify-center">
                                                <Button
                                                    type="plain"
                                                    size="sm"
                                                    iconOnly
                                                    leftIcon={<TrashIcon className="w-5 h-5" />}
                                                    ariaLabel="Eliminar opción"
                                                    className="text-red-500"
                                                    onClick={() => {
                                                        const items = editingList.items.filter((_, i) => i !== index);
                                                        setEditingList({ ...editingList, items });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    {editingList.items.length === 0 && (
                                        <div className="w-full py-8 text-center text-sm text-gray-400 border-b border-gray-200">
                                            No hay opciones agregadas. Haz clic en "Nuevo" para comenzar.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="px-6 py-4 flex justify-end gap-3 w-full border-t border-gray-100 bg-gray-50 shrink-0">
                            <Button
                                type="default"
                                onClick={handleSave}
                            >
                                Guardar
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
